import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Building2, CheckCircle2, XCircle, Loader2, RefreshCw, LogOut, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter49 P0 — Google Business Profile OAuth completion page.
 * Owner exchanges code, sees list of accounts/verified locations, and
 * chooses ONE location. Reconnect and Change Location supported.
 */
export default function GbpOAuthCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("exchanging");
  const [accounts, setAccounts] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [selecting, setSelecting] = useState(null);
  const [connection, setConnection] = useState(null);

  useEffect(() => {
    (async () => {
      const err = params.get("error");
      if (err) {
        setErrorMsg(`Google returned "${err}". Retry from Business Settings.`);
        setPhase("error");
        return;
      }
      const code = params.get("code");
      const state = params.get("state");
      const savedState = sessionStorage.getItem("gbp_oauth_state");
      if (!code) {
        setErrorMsg("Missing OAuth code. Return to Business Settings and start again.");
        setPhase("error");
        return;
      }
      if (savedState && state && state !== savedState) {
        setErrorMsg("OAuth state mismatch — possible CSRF. Start again from Business Settings.");
        setPhase("error");
        return;
      }
      try {
        await api.post("/gbp/oauth/callback", { code, state });
        const { data } = await api.get("/gbp/accounts-and-locations");
        const eligible = (data.accounts || []).filter(a => (a.locations || []).length > 0);
        if (eligible.length === 0) {
          setErrorMsg("No verified locations were found for your Business Profile account. Verify a location on Google Business Profile, then reconnect.");
          setPhase("error");
          return;
        }
        setAccounts(eligible);
        setPhase("selecting");
      } catch (e) {
        const msg = e.response?.data?.detail || "Google token exchange failed — retry from Business Settings.";
        setErrorMsg(msg);
        setPhase("error");
      } finally {
        sessionStorage.removeItem("gbp_oauth_state");
      }
    })();
  }, [params]);

  const selectLocation = async (acct, loc) => {
    setSelecting(loc.name);
    try {
      const { data } = await api.post("/gbp/locations/select", {
        account_id: acct.account_id,
        account_name: acct.account_name,
        location_id: loc.name,
        location_title: loc.title,
        new_review_uri: loc.new_review_uri || null,
      });
      setConnection({ account: acct, location: loc, ...data });
      setPhase("success");
      toast.success(`Connected to “${loc.title}”`);
      // Iter P2-gbp1 — if the owner already pasted a custom review link,
      // offer to swap it for Google's canonical newReviewUri.
      if (data?.review_url_suggestion) {
        const useGoogle = window.confirm(
          `A Google review link is available for “${loc.title}”:\n\n${data.review_url_suggestion}\n\n` +
          `You currently have a custom review link saved. Replace it with Google's?`
        );
        if (useGoogle) {
          try {
            await api.post("/gbp/review-url/swap-to-google", { new_review_uri: data.review_url_suggestion });
            toast.success("Review link updated to Google's canonical URL");
          } catch { toast.error("Could not update review link"); }
        }
      } else if (data?.review_url_set) {
        toast.success("Google review link auto-filled in Settings");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Location selection failed");
    } finally { setSelecting(null); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Business Profile? Existing posts will remain on your profile.")) return;
    try {
      await api.post("/gbp/disconnect");
      toast.success("Disconnected from Google Business Profile");
      nav("/app/settings");
    } catch { toast.error("Disconnect failed"); }
  };

  return (
    <div className="max-w-3xl mx-auto p-8" data-testid="gbp-oauth-callback-page">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="w-6 h-6 text-emerald-700"/>
        <h1 className="font-heading text-2xl font-bold">Google Business Profile</h1>
      </div>

      {phase === "exchanging" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center card-crisp" data-testid="gbp-oauth-exchanging">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5A00] mx-auto mb-3"/>
          <div className="font-medium">Verifying with Google…</div>
          <div className="text-sm text-zinc-500 mt-1">Fetching accounts and verified locations.</div>
        </div>
      )}

      {phase === "selecting" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="gbp-oauth-location-picker">
          <div className="font-medium mb-1">Select the verified location to publish to</div>
          <div className="text-sm text-zinc-500 mb-4">Refresh token stored encrypted. You can change location later without full reconnect.</div>
          <div className="space-y-4">
            {accounts.map((acct) => (
              <div key={acct.account_id}>
                <div className="text-xs uppercase text-zinc-500 mb-2 font-mono-accent">{acct.account_name}</div>
                <div className="space-y-2">
                  {acct.locations.map((loc) => (
                    <button
                      key={loc.name}
                      onClick={() => selectLocation(acct, loc)}
                      disabled={!!selecting || !loc.has_voice_of_merchant}
                      className="w-full text-left border border-zinc-200 rounded-lg p-3 hover:border-emerald-600 hover:bg-emerald-50 transition-colors flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`gbp-location-option-${loc.name.replace(/\W/g,'-')}`}
                    >
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-emerald-700"/> {loc.title}
                          {loc.has_voice_of_merchant && <ShieldCheck className="w-4 h-4 text-emerald-600" title="Verified"/>}
                        </div>
                        <div className="text-xs text-zinc-500 font-mono-accent">{loc.name} · {loc.voice_of_merchant_state || "state unknown"}</div>
                      </div>
                      {selecting === loc.name ? <Loader2 className="w-4 h-4 animate-spin"/> : <span className="text-emerald-700 text-sm">Select →</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {phase === "success" && (
        <div className="bg-white border border-emerald-200 rounded-xl p-6 card-crisp" data-testid="gbp-oauth-success">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
            <div className="font-medium">Connected!</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">
            LeadForge is linked to <span className="font-medium">{connection?.location?.title}</span>. You can now publish Local Posts and (if enabled) monitor reviews.
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => nav("/app/settings")} data-testid="gbp-oauth-done">
              Back to Settings
            </Button>
            <Button variant="outline" onClick={() => nav(0)} data-testid="gbp-oauth-change">
              <RefreshCw className="w-4 h-4 mr-1"/> Change Location
            </Button>
            <Button variant="outline" onClick={disconnect} data-testid="gbp-oauth-disconnect">
              <LogOut className="w-4 h-4 mr-1"/> Disconnect
            </Button>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="bg-white border border-red-200 rounded-xl p-6 card-crisp" data-testid="gbp-oauth-error">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-600"/>
            <div className="font-medium">Couldn't finish the connection</div>
          </div>
          <div className="text-sm text-zinc-600 mb-4">{errorMsg}</div>
          <Button onClick={() => nav("/app/settings")}>Back to Settings</Button>
        </div>
      )}
    </div>
  );
}
